import { z } from "zod";

export const DiscordAuditDestinationTypeSchema = z.enum(["webhook", "channel"]);
export type DiscordAuditDestinationType = z.infer<typeof DiscordAuditDestinationTypeSchema>;

const actionKeysSchema = z.array(z.string().min(1).max(64)).max(500);
const webhookUrlSchema = z
  .string()
  .trim()
  .min(1)
  .max(512)
  .regex(
    /^https:\/\/discord(?:app)?\.com\/api\/webhooks\/\d{17,20}\/[A-Za-z0-9_-]{20,200}$/,
    "Enter a valid Discord webhook URL.",
  );
const channelIdSchema = z.string().regex(/^\d{17,20}$/, "Choose a text channel from this server.");

export const CreateDiscordAuditDestinationSchema = z
  .object({
    name: z.string().trim().min(1).max(100),
    destinationType: DiscordAuditDestinationTypeSchema,
    webhookUrl: webhookUrlSchema.optional(),
    channelId: channelIdSchema.optional(),
    enabled: z.boolean().default(true),
    logAllActions: z.boolean().default(false),
    actionKeys: actionKeysSchema.default([]),
  })
  .superRefine((value, ctx) => {
    if (value.destinationType === "webhook" && !value.webhookUrl) {
      ctx.addIssue({ code: "custom", path: ["webhookUrl"], message: "Webhook URL is required." });
    }
    if (value.destinationType === "channel" && !value.channelId) {
      ctx.addIssue({ code: "custom", path: ["channelId"], message: "Text channel is required." });
    }
    if (value.destinationType === "webhook" && value.channelId) {
      ctx.addIssue({
        code: "custom",
        path: ["channelId"],
        message: "Choose one destination type.",
      });
    }
    if (value.destinationType === "channel" && value.webhookUrl) {
      ctx.addIssue({
        code: "custom",
        path: ["webhookUrl"],
        message: "Choose one destination type.",
      });
    }
    if (!value.logAllActions && value.actionKeys.length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["actionKeys"],
        message: "Choose actions or enable logging for every action.",
      });
    }
  });
export type CreateDiscordAuditDestination = z.infer<typeof CreateDiscordAuditDestinationSchema>;

export const UpdateDiscordAuditDestinationSchema = z
  .object({
    name: z.string().trim().min(1).max(100).optional(),
    enabled: z.boolean().optional(),
    logAllActions: z.boolean().optional(),
    actionKeys: actionKeysSchema.optional(),
  })
  .refine((value) => Object.keys(value).length > 0, "Provide at least one setting to update.");
export type UpdateDiscordAuditDestination = z.infer<typeof UpdateDiscordAuditDestinationSchema>;

export const DiscordAuditDestinationSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  destinationType: DiscordAuditDestinationTypeSchema,
  destinationLabel: z.string(),
  enabled: z.boolean(),
  logAllActions: z.boolean(),
  actionKeys: actionKeysSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DiscordAuditDestination = z.infer<typeof DiscordAuditDestinationSchema>;

export const DiscordAuditChannelSchema = z.object({
  id: channelIdSchema,
  name: z.string(),
  categoryName: z.string().nullable(),
});
export type DiscordAuditChannel = z.infer<typeof DiscordAuditChannelSchema>;

export const DepartmentCocRoleSchema = z.object({
  departmentId: z.number().int(),
  departmentName: z.string(),
  roleId: z.string().regex(/^\d{17,20}$/),
  roleName: z.string(),
});
export type DepartmentCocRole = z.infer<typeof DepartmentCocRoleSchema>;

export const UpdateDepartmentCocRolesSchema = z.object({
  roleIds: z.array(z.string().regex(/^\d{17,20}$/)).max(30),
});

export const DepartmentCocSettingsSchema = z.object({
  departments: z.array(
    z.object({
      id: z.number().int(),
      name: z.string(),
      isActive: z.boolean(),
      roleIds: z.array(z.string()),
      roleNames: z.array(z.string()),
    }),
  ),
  roles: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      color: z.number().int(),
      position: z.number().int(),
    }),
  ),
});
export type DepartmentCocSettings = z.infer<typeof DepartmentCocSettingsSchema>;
